import React, { useState } from "react";
import { FileText, FileSpreadsheet, Loader2, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, openPdf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

export default function StatementExport({ customerId, customer, disabled, onShared }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const [confirmMail, setConfirmMail] = useState(false);
  const params = { date_from: from || undefined, date_to: to || undefined };

  const sendMail = async () => {
    setConfirmMail(false);
    setBusy("mail");
    try {
      const { data } = await api.post(`/ledger/statement/${customerId}/send-email`, null, { params });
      toast.success(data.message);
      onShared && onShared();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const openWhatsApp = async () => {
    setBusy("wa");
    try {
      const { data } = await api.get(`/ledger/statement/${customerId}/whatsapp`, { params });
      if (!data.phone) toast.warning("Müşteri telefonu yok — WhatsApp'ta alıcıyı kendiniz seçin");
      window.open(data.url, "_blank", "noopener");
      onShared && onShared();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const download = async (format) => {
    setBusy(format);
    try {
      const res = await api.get(`/ledger/export/${customerId}`, {
        params: { format, ...params },
        responseType: "blob",
      });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      if (format === "pdf") {
        openPdf(res.data);
      } else {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url;
        a.download = m ? m[1] : `ekstre.${format}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
      toast.success(format === "pdf" ? "PDF ekstre açıldı" : "Excel ekstre indirildi");
      onShared && onShared();
    } catch {
      toast.error("Ekstre oluşturulamadı");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-2 sm:items-center" data-testid="statement-export">
      <div className="flex items-center gap-2">
        <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-11 w-[150px] font-mono" data-testid="statement-from" />
        <span className="text-muted-foreground text-sm">—</span>
        <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-11 w-[150px] font-mono" data-testid="statement-to" />
      </div>
      <div className="flex gap-2">
        <Button variant="outline" disabled={disabled || !!busy} onClick={() => download("pdf")} data-testid="export-pdf-btn" className="h-11 gap-2">
          {busy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-rose-400" />} PDF Görüntüle
        </Button>
        <Button variant="outline" disabled={disabled || !!busy} onClick={() => download("xlsx")} data-testid="export-xlsx-btn" className="h-11 gap-2">
          {busy === "xlsx" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 text-emerald-400" />} Excel İndir
        </Button>
        <Button variant="outline" disabled={disabled || !!busy} onClick={() => setConfirmMail(true)} data-testid="share-email-btn" className="h-11 gap-2"
          title={customer?.email ? `${customer.email} adresine gönder` : "Müşteri kartında e-posta yok"}>
          {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 text-sky-400" />} E-posta Gönder
        </Button>
        <Button variant="outline" disabled={disabled || !!busy} onClick={openWhatsApp} data-testid="share-whatsapp-btn" className="h-11 gap-2">
          {busy === "wa" ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4 text-emerald-500" />} WhatsApp
        </Button>
      </div>

      <AlertDialog open={confirmMail} onOpenChange={setConfirmMail}>
        <AlertDialogContent data-testid="share-email-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Ekstre e-posta ile gönderilsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {customer?.email
                ? <>Ekstre özeti ve PDF eki <b>{customer.email}</b> adresine gönderilecek.</>
                : "Bu müşterinin kartında e-posta adresi yok. Önce Müşteriler sayfasından e-posta ekleyin."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            {customer?.email && <AlertDialogAction onClick={sendMail} data-testid="share-email-confirm-btn">Gönder</AlertDialogAction>}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
